import React from 'react';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';

import { EDITOR_UI_ATTRIBUTE, STOP_HANDLERS } from '@/components/TextOverrides/editorUi';

/*
 * Bottom centre, the one free slot on every screen: the right edge belongs to the chat and events-history drawers
 * (their composers sit bottom-right), toasts are top-right, and bottom-left holds the sidebar footer (support,
 * settings) and the React Query devtools toggle. Above app dialogs and menus (theme token), below the text editor.
 */
const Slot = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(1),
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: theme.zIndex.textOverridesOverlay,
}));

const Banner = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 0.75, 0.75, 2),
  maxWidth: `calc(100vw - ${theme.spacing(4)})`,
  whiteSpace: 'nowrap',
  borderRadius: 999,
  background: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
}));

interface Props {
  readonly editMode: boolean;
  readonly organizationName: string;
  readonly onToggle: () => void;
}

/**
 * POC ONLY: an always-visible edit mode toggle for every signed-in user, with no permission or feature gate. Remove it
 * (or gate it) before production; the account menu entry and Ctrl/Cmd+Shift+E remain. In edit mode it becomes the
 * banner naming the organization. English literals, under the unmarked intl, excluded from the pencil scan.
 */
const EditModeControl = ({ editMode, organizationName, onToggle }: Props) => (
  <Slot {...{ [EDITOR_UI_ATTRIBUTE]: '' }} {...STOP_HANDLERS}>
    {editMode ? (
      <Banner elevation={6} role="status">
        <EditOutlinedIcon fontSize="small" />
        <Typography variant="body2" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
          Editing texts for {organizationName}
        </Typography>
        <Typography variant="body2" noWrap sx={{ opacity: 0.8, flexShrink: 0 }}>
          · Click a pencil to change a text · Esc
        </Typography>
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          onClick={onToggle}
          startIcon={<EditOutlinedIcon />}
          sx={{ ml: 1, borderRadius: 999 }}
        >
          Exit edit mode
        </Button>
      </Banner>
    ) : (
      <Fab variant="extended" size="small" color="primary" onClick={onToggle} aria-label="Edit mode">
        <EditOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
        Edit mode
      </Fab>
    )}
  </Slot>
);

export default EditModeControl;
